import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Menu, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from '@/components/ui/navigation-menu';
import { useCart } from '@/state/cart';
import { useCatalog } from '@/state/catalog';
import { formatNumber } from '@/lib/api';
import { productImage } from '@/lib/images';
import { Picture } from './Picture';
import { cn } from '@/lib/utils';
import { BrandLogo } from '@/components/brand/BrandLogo';

/*
  Offentlig toppfelt.
  - Øverst på siden: full bredde. På forsiden gjennomsiktig over hero-bildet (hvit tekst).
  - Ved rulling: trekker seg sammen til en flytende, frostet «øy» med hårlinje og myk skygge.
  - «Produkter og priser» åpner en meny med kategoriene (reelle tall fra katalogen) og Big bag først.
  Høyden på selve <header> er konstant (64/72 px) slik at hero-bildet kan trekkes opp under den.
*/

const NAV = [
  { to: '/produkter', label: 'Produkter og priser', slug: 'produkter', menu: true },
  { to: '/#levering', label: 'Levering', slug: 'levering' },
  { to: '/#dokumentasjon', label: 'Dokumentasjon', slug: 'dokumentasjon' },
  { to: '/#om-oss', label: 'Om oss', slug: 'om' },
  { to: '/#kontakt', label: 'Kontakt', slug: 'kontakt' },
];

const CATEGORY_TEXT = {
  spesial: 'Big bag, plastringsstein, murestein og sprengt røys.',
  sand: 'Knust sand 0/6 og 0/8 til grøfter, avretting og betong.',
  grus: 'Toppdekke, slitelag og bærelag 0/11–0/32.',
  pukk: 'Drenering, forsterkning og frostsikring 8/16–20/120.',
};

function isQuote(p) {
  return p.priceType === 'quote' || p.pricePerTonne === null || p.pricePerTonne === undefined;
}

/** Innhold i produktmenyen: kategorier med antall og laveste tonnpris, pluss Big bag som fremhevet kort. */
function ProductsMenu({ catalog, onNavigate }) {
  const groups = useMemo(
    () =>
      (catalog.categories ?? []).map((c) => {
        const items = (catalog.products ?? []).filter((p) => p.category === c.id);
        const fixed = items.filter((p) => !isQuote(p)).map((p) => p.pricePerTonne);
        return { ...c, count: items.length, from: fixed.length ? Math.min(...fixed) : null };
      }),
    [catalog.categories, catalog.products],
  );
  const featured = (catalog.products ?? [])[0];
  return (
    <div className="grid w-[min(92vw,760px)] grid-cols-[1.25fr_1fr] gap-2 p-2" data-testid="site-products-menu">
      <ul className="grid gap-0.5">
        {groups.map((g) => (
          <li key={g.id}>
            <NavigationMenuLink asChild>
              <Link to={`/produkter?kategori=${g.id}`} onClick={onNavigate} className="group flex items-start justify-between gap-4 rounded-[12px] px-3.5 py-3 transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={`site-menu-category-${g.id}`}>
                <span className="min-w-0">
                  <span className="block text-[14.5px] font-semibold text-[var(--hr-heading)]">{g.label}</span>
                  <span className="mt-0.5 block text-[13px] leading-5 text-[var(--hr-muted)]">{CATEGORY_TEXT[g.id] ?? ''}</span>
                </span>
                <span className="hr-tabular shrink-0 pt-0.5 text-right text-[12.5px] text-[var(--hr-muted)]">
                  {g.from !== null ? (
                    <>
                      fra <span className="font-semibold text-[var(--hr-heading)]">{formatNumber(g.from)}</span> kr/t
                    </>
                  ) : (
                    'Pris på forespørsel'
                  )}
                  <span className="block">{g.count} produkter</span>
                </span>
              </Link>
            </NavigationMenuLink>
          </li>
        ))}
        <li className="mt-1 border-t border-[var(--hr-hairline)] pt-1">
          <NavigationMenuLink asChild>
            <Link to="/produkter" onClick={onNavigate} className="flex items-center justify-between rounded-[12px] px-3.5 py-2.5 text-[14px] font-semibold text-[var(--hr-heading)] transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid="site-menu-all-products">
              Se alle produkter og priser <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </NavigationMenuLink>
        </li>
      </ul>
      {featured && (
        <NavigationMenuLink asChild>
          <Link to={`/produkter/${featured.slug}`} onClick={onNavigate} className="site-photo group relative flex min-h-[260px] flex-col justify-end overflow-hidden rounded-[14px] bg-[var(--site-ink)] p-4 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid="site-menu-featured">
            <Picture variant={productImage(featured.image)} sizes="320px" alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" />
            <span className="absolute inset-0 bg-[linear-gradient(180deg,rgba(27,28,34,0)_30%,rgba(27,28,34,0.85)_100%)]" aria-hidden="true" />
            <span className="relative">
              <span className="site-mono block text-[11.5px] text-white/70">{featured.fraction}</span>
              <span className="mt-1 flex items-end justify-between gap-3">
                <span className="font-display text-[22px] font-semibold leading-none tracking-[-0.02em]">{featured.name}</span>
                <span className="inline-flex items-center gap-1 text-[13px] font-medium">
                  {isQuote(featured) ? 'Be om tilbud' : `${formatNumber(featured.pricePerTonne)} kr/t`} <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
              </span>
            </span>
          </Link>
        </NavigationMenuLink>
      )}
    </div>
  );
}

function DesktopNav({ inverted, catalog }) {
  const location = useLocation();
  const [hover, setHover] = useState(null);
  const itemClass = (active) =>
    cn(
      'relative isolate inline-flex h-9 items-center rounded-full px-3.5 text-[14px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]',
      inverted ? (active ? 'text-white' : 'text-white/80 hover:text-white') : active ? 'text-[var(--hr-heading)]' : 'text-[var(--hr-muted)] hover:text-[var(--hr-heading)]',
    );
  const pill = (slug) =>
    hover === slug && <motion.span layoutId="site-nav-pill" className={cn('absolute inset-0 -z-10 rounded-full', inverted ? 'bg-white/15' : 'bg-[rgba(31,33,48,0.06)]')} transition={{ type: 'spring', stiffness: 520, damping: 42, mass: 0.6 }} aria-hidden="true" />;

  return (
    <NavigationMenu className="hidden lg:flex" aria-label="Hovedmeny" onMouseLeave={() => setHover(null)}>
      <NavigationMenuList className="gap-0.5">
        {NAV.map((n) => {
          const active = !n.to.includes('#') && location.pathname.startsWith(n.to);
          if (n.menu) {
            return (
              <NavigationMenuItem key={n.slug} onMouseEnter={() => setHover(n.slug)}>
                <NavigationMenuTrigger className={cn(itemClass(active), 'h-9 bg-transparent px-3.5 text-[14px] hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:hover:bg-transparent data-[state=open]:focus:bg-transparent', inverted ? 'data-[state=open]:text-white' : 'data-[state=open]:text-[var(--hr-heading)]')} data-testid="site-nav-produkter">
                  {pill(n.slug)}
                  {n.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ProductsMenu catalog={catalog} />
                </NavigationMenuContent>
              </NavigationMenuItem>
            );
          }
          return (
            <NavigationMenuItem key={n.slug} onMouseEnter={() => setHover(n.slug)}>
              <NavigationMenuLink asChild>
                <a href={n.to} className={itemClass(active)} data-testid={`site-nav-${n.slug}`}>
                  {pill(n.slug)}
                  {n.label}
                </a>
              </NavigationMenuLink>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
}

function MobileNavItem({ item, onClick }) {
  const location = useLocation();
  const isHash = item.to.includes('#');
  const active = !isHash && location.pathname.startsWith(item.to);
  const cls = cn('flex h-12 items-center rounded-[10px] px-3 text-[17px] font-medium text-[var(--hr-text)] transition-colors duration-150 hover:bg-[var(--hr-surface-2)]', active && 'bg-[var(--hr-accent-soft)]');
  const testId = `mobile-site-nav-${item.slug}`;
  if (isHash) {
    return (
      <a href={item.to} onClick={onClick} className={cls} data-testid={testId}>
        {item.label}
      </a>
    );
  }
  return (
    <NavLink to={item.to} onClick={onClick} className={cls} data-testid={testId}>
      {item.label}
    </NavLink>
  );
}

export function SiteHeader({ transparent = false }) {
  const cart = useCart();
  const catalog = useCatalog();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const inverted = transparent && !scrolled;
  const solid = !inverted;

  return (
    <header className="sticky top-0 z-[var(--z-topbar)] h-16 sm:h-[72px]" data-testid="site-header" data-solid={solid ? 'true' : 'false'} data-floating={scrolled ? 'true' : 'false'}>
      {/* Full bredde på alle skjermer. Gjennomsiktig over hero, frostet hvit flate med hårlinje ved rulling. */}
      <div
        className={cn(
          'relative flex h-16 w-full items-center gap-3 px-5 transition-[background-color,border-color,box-shadow] duration-200 [transition-timing-function:var(--hr-ease)] sm:h-[72px] sm:px-8 lg:px-10',
          scrolled
            ? 'border-b border-[rgba(20,23,42,0.08)] bg-white/85 shadow-[0_10px_30px_-18px_rgba(16,24,40,0.25)] backdrop-blur-xl supports-[backdrop-filter]:bg-white/78'
            : cn('border-b border-transparent', !transparent && 'border-[var(--hr-hairline)] bg-white'),
        )}
      >
        <Link to="/" className="flex shrink-0 items-center rounded-md" aria-label="Hardanger Rock – til forsiden" data-testid="site-logo-link">
          <BrandLogo variant={inverted ? 'whiteOrange' : 'dark'} className="h-8 w-auto sm:h-9" testId="site-header-logo" />
        </Link>

        <div className="ml-4 hidden lg:flex xl:absolute xl:left-1/2 xl:ml-0 xl:-translate-x-1/2">
          <DesktopNav inverted={inverted} catalog={catalog} />
        </div>

        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="icon" className={cn('relative h-10 w-10 rounded-full', inverted ? 'text-white hover:bg-white/10 hover:text-white' : 'text-[var(--hr-text)] hover:bg-[rgba(31,33,48,0.06)]')} onClick={() => cart.setOpen(true)} aria-label={`Bestilling, ${cart.count} produkter`} data-testid="cart-open-button">
            <ShoppingBag className="h-[18px] w-[18px]" aria-hidden="true" />
            {cart.count > 0 && (
              <span className="hr-tabular absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[var(--site-orange)] px-1.5 text-[11px] font-bold text-[var(--site-ink)]" data-testid="cart-count-badge">
                {cart.count}
              </span>
            )}
          </Button>
          <Button asChild variant="ghost" className={cn('hidden h-10 rounded-full px-3.5 text-[14px] font-medium md:inline-flex', inverted ? 'text-white/90 hover:bg-white/10 hover:text-white' : 'text-[var(--hr-text)] hover:bg-[rgba(31,33,48,0.06)]')}>
            <Link to="/login" data-testid="site-login-link">
              Portal-demo
            </Link>
          </Button>
          <Button asChild className={cn('hidden h-10 rounded-full px-[18px] text-[14px] font-semibold shadow-none transition-colors duration-150 sm:inline-flex', inverted ? 'bg-white text-[var(--site-ink)] hover:bg-white/90' : 'bg-[var(--site-ink)] text-white hover:bg-[var(--site-ink-2)]')}>
            <Link to="/produkter" data-testid="site-order-link">
              Bestill masser <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <Button variant="ghost" size="icon" className={cn('h-10 w-10 rounded-full lg:hidden', inverted ? 'text-white hover:bg-white/10 hover:text-white' : 'text-[var(--hr-text)] hover:bg-[rgba(31,33,48,0.06)]')} onClick={() => setOpen(true)} aria-label="Åpne meny" data-testid="site-mobile-menu-button">
              <Menu className="h-5 w-5" aria-hidden="true" />
            </Button>
            <SheetContent side="right" className="w-[320px] bg-white p-0" data-testid="site-mobile-menu">
              <SheetHeader className="sr-only">
                <SheetTitle>Meny</SheetTitle>
              </SheetHeader>
              <div className="flex h-16 items-center border-b border-[var(--hr-hairline)] px-5">
                <BrandLogo variant="dark" className="h-8 w-auto" testId="site-menu-logo" />
              </div>
              <nav aria-label="Hovedmeny" className="flex flex-col gap-1 p-3">
                {NAV.map((n) => (
                  <MobileNavItem key={n.to} item={n} onClick={() => setOpen(false)} />
                ))}
                <Link to="/prosjekt" onClick={() => setOpen(false)} className="flex h-12 items-center rounded-[10px] px-3 text-[17px] font-medium text-[var(--hr-text)] hover:bg-[var(--hr-surface-2)]" data-testid="mobile-site-nav-prosjekt">
                  Be om tilbud
                </Link>
              </nav>
              <div className="mt-2 flex flex-col gap-2 border-t border-[var(--hr-hairline)] p-5">
                <Button asChild className="h-11 w-full rounded-full bg-[var(--site-orange)] text-[15px] font-semibold text-[var(--site-ink)] hover:bg-[var(--site-orange-2)]">
                  <Link to="/produkter" onClick={() => setOpen(false)} data-testid="mobile-site-order-link">
                    Bestill masser
                  </Link>
                </Button>
                <Button asChild variant="outline" className="h-11 w-full rounded-full text-[15px] font-semibold">
                  <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-site-login-link">
                    Åpne produksjonsportalen (demo)
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
